import { Button, Form, Input, InputNumber, Tag } from 'antd';
import { useFormikContext } from 'formik';
import { FV } from './share';
import FormField from 'components/FormField';
import { regionMonetaryUnitMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { getRecommendSlogans } from '@knowledge/service/api/activity';
import { noop } from 'lodash-es';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { useTranslate } from '@tolgee/react';

function Slogans({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslate();

  const {
    values: { activityType, stores, slogans, items, recommendItems },
    setValues,
  } = useFormikContext<FV>();

  const region = stores[0] ? stores[0].region : undefined;
  const unit = region ? regionMonetaryUnitMap[toLowerCase(region)] : undefined;

  const finalItems = recommendItems?.length ? recommendItems : items;

  const lang = useSystemLanguageValue();

  const [recommendSlogans, setRecommendSlogans] = useState<string[]>([]);
  const [translatedSlogans, setTranslatedSlogans] = useState<string[]>([]);

  useEffect(() => {
    if (finalItems.length) {
      getRecommendSlogans(finalItems).then(setRecommendSlogans).catch(noop);
    } else {
      setRecommendSlogans([]);
    }
  }, [finalItems]);
  useEffect(() => {
    if (recommendSlogans && recommendSlogans.length) {
      overseaTranslate(recommendSlogans, lang, 'translateTool')
        .then(setTranslatedSlogans)
        .catch(noop);
    } else {
      setTranslatedSlogans([]);
    }
  }, [recommendSlogans, lang]);

  const tips = useMemo(() => {
    const [, prefix, item1, item2, item3, suffix] =
      t(
        '小乐建议您在[上架商品/促销商品/活动商品]中，挑选价格最具吸引力的1-3款商品作为引流款，帮助买家更直观地感受活动的优惠力度',
      ).match(/^(.*)\[(.*)\/(.*)\/(.*)\](.*)$/) || [];

    if (prefix && suffix) {
      return `${prefix}${
        activityType === 'NEW_PRODUCT_ONSALE_ACTIVITY'
          ? item1
          : activityType === 'CLEARANCE_SALE_ACTIVITY' || activityType === 'LIVE_STREAMING_ACTIVITY'
          ? item2
          : item3
      }${suffix}`;
    }
  }, [t, activityType]);

  return (
    <Form.Item label=" ">
      <Container>
        <span>{tips}</span>
        <div>
          {slogans?.map(({ item, originPrice, currentPrice }, index) => {
            return (
              <Item>
                <FormItem>
                  <span>{t('商品简称')}</span>
                  <FormField<Required<FV>['slogans'][0]['item']>
                    name={`slogans.${index}.item`}
                    validate={(value) =>
                      value || !currentPrice
                        ? undefined
                        : t('department.请输入x', { x: t('商品简称') })
                    }
                  >
                    {({ field }) => {
                      return (
                        <div>
                          <Input
                            disabled={disabled}
                            showCount
                            maxLength={50}
                            placeholder={t('建议输入当地语言名称')}
                            {...field}
                          />
                          {translatedSlogans.length > 0 && (
                            <Recommend>
                              <span>{t('推荐简称')}：</span>
                              <div>
                                {translatedSlogans.map((slogan) => (
                                  <Tag color="blue" onClick={() => field.onChange(slogan)}>
                                    {slogan}
                                  </Tag>
                                ))}
                              </div>
                            </Recommend>
                          )}
                        </div>
                      );
                    }}
                  </FormField>
                </FormItem>
                <FormItem>
                  <span>{t('原价')}</span>
                  <FormField<Required<FV>['slogans'][0]['originPrice']>
                    name={`slogans.${index}.originPrice`}
                  >
                    {({ field: { value, onChange, ...others } }) => {
                      return (
                        <InputNumber
                          disabled={disabled}
                          placeholder={t('选填')}
                          value={value ? Number(value) ?? undefined : undefined}
                          onChange={(value) =>
                            onChange(typeof value === 'number' ? String(value) : '')
                          }
                          {...others}
                          addonAfter={unit}
                        />
                      );
                    }}
                  </FormField>
                </FormItem>
                <FormItem>
                  <span>{t('仅售')}</span>
                  <FormField<Required<FV>['slogans'][0]['currentPrice']>
                    name={`slogans.${index}.currentPrice`}
                    validate={(value) => {
                      if (item && !value) {
                        return t('department.请输入x', { x: t('售卖价') });
                      }

                      if (originPrice && Number(value) > Number(originPrice)) {
                        return t('仅售价不得高于原价');
                      }
                    }}
                  >
                    {({ field: { value, onChange, ...others } }) => {
                      return (
                        <InputNumber
                          disabled={disabled}
                          placeholder={t('售卖价')}
                          value={value ? Number(value) ?? undefined : undefined}
                          onChange={(value) =>
                            onChange(typeof value === 'number' ? String(value) : '')
                          }
                          {...others}
                          addonAfter={unit}
                        />
                      );
                    }}
                  </FormField>
                </FormItem>
                <span>
                  <Button
                    disabled={disabled}
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setValues((values) =>
                        values.slogans
                          ? {
                              ...values,
                              slogans: [
                                ...values.slogans.slice(0, index),
                                ...values.slogans.slice(index + 1),
                              ],
                            }
                          : values,
                      )
                    }
                  />
                </span>
              </Item>
            );
          })}
          <footer>
            <Button
              disabled={disabled}
              type="text"
              icon={<PlusOutlined />}
              onClick={() =>
                setValues((values) => ({
                  ...values,
                  slogans: [
                    ...(values.slogans || []),
                    { item: '', currentPrice: '', originPrice: '' },
                  ],
                }))
              }
            >
              {t('添加引流款')}
            </Button>
          </footer>
        </div>
      </Container>
    </Form.Item>
  );
}

const Container = styled.div`
  max-width: 800px;
  > span {
    display: block;
    padding: 6px 10px;
    font-size: 12px;
    color: var(--fett-text-tertiary);
    background: var(--fett-bg-info-tip);
  }
  > div {
    border: solid 12px var(--fett-bg-panel);
    > footer {
      display: flex;
      justify-content: flex-end;
      padding: 4px 8px;
    }
  }
`;

const Item = styled.div`
  position: relative;
  display: flex;
  gap: 16px;
  padding: 12px 40px 0 7px;
  border-bottom: solid 1px var(--fett-border-default);
  > * {
    flex: 3 0 0;
  }
  > *:first-of-type {
    flex: 5 0 0;
  }
  > span {
    position: absolute;
    top: 0;
    right: 0;
    width: 32px;
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: var(--fett-bg-info-tip);
  }
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  > span {
    margin-bottom: 4px;
  }
  > div {
    .ant-input-affix-wrapper,
    .ant-input-number,
    .ant-input-number-group-wrapper {
      width: 100%;
    }
  }
`;

const Recommend = styled.div`
  display: flex;
  padding: 2px 4px;
  border: 1px solid var(--fett-tag-gray-border);
  border-top: none;
  > span {
    flex: 0 0 auto;
    height: 18px;
    line-height: 24px;
    font-size: 12px;
    color: var(--fett-text-tertiary);
  }
  > div {
    flex: 1 0 0;
    .ant-tag {
      line-height: 16px;
      font-size: 12px;
      white-space: pre-wrap;
      background: transparent;
      cursor: pointer;
    }
  }
`;

export default Slogans;
